import { useState } from 'react';

import { NotificationRule } from '@/features/notification-rules/schemas/notification-rules.schema.ts';
import { Button } from '@/components/ui/button';
import {
  Credenza,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { useDeleteNotificationRuleMutation } from '@/features/notification-rules/hooks/mutations/use-delete-notification-rule.ts';
import { LoaderIcon } from 'lucide-react';

export function NotificationRulesTableRowActionDeleteDialog({
  notificationRule,
}: {
  notificationRule: NotificationRule;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { mutate: deleteNotificationRuleMutation, isPending: isDeletePending } =
    useDeleteNotificationRuleMutation({
      onSuccess: () => {
        setIsDialogOpen(false);
      },
    });

  const onClickDelete = () => {
    deleteNotificationRuleMutation({
      notificationRuleId: notificationRule.notificationRuleId,
    });
  };

  return (
    <Credenza open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <CredenzaTrigger asChild>
        <button className="focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors select-none hover:bg-gray-100 data-disabled:pointer-events-none data-disabled:opacity-50">
          <span>Delete</span>
        </button>
      </CredenzaTrigger>
      <CredenzaContent>
        <CredenzaHeader>
          <CredenzaTitle>Confirm Notification Rule Deletion</CredenzaTitle>
          <CredenzaDescription>
            Are you sure you want to delete{' '}
            <span className="font-medium">{notificationRule.notificationTitle}</span>{' '}
            notification rule? This action cannot be undone.
          </CredenzaDescription>
        </CredenzaHeader>
        <CredenzaFooter className="gap-2 sm:space-x-0">
          <CredenzaClose asChild>
            <Button variant="outline">Cancel</Button>
          </CredenzaClose>
          <Button
            aria-label="Delete selected rows"
            variant="destructive"
            onClick={onClickDelete}
            disabled={isDeletePending}
          >
            {isDeletePending && (
              <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
            )}
            Delete
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
}

export default NotificationRulesTableRowActionDeleteDialog;
